import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Building, 
  Shield, 
  Plus, 
  Minus, 
  Edit3, 
  Check, 
  X,
  AlertCircle,
  Save,
  UserCheck,
  Send,
  ExternalLink,
  Copy,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { User as UserType, ApprovalRequest } from '../types/User';
import EmailService from '../services/EmailService';
import ApprovalTokenManager from '../utils/ApprovalTokenManager';
import { approvalAPI } from '../services/ApprovalAPI';
import localAuthService from '../services/LocalAuthService';

interface RoleManagementProps {
  user: UserType;
  action: 'add' | 'remove' | 'change' | 'view';
  onUserUpdate: (user: UserType) => void;
  onRequestCreated: (request: ApprovalRequest) => void;
}

interface SapRole {
  RoleId: string;
  RoleName: string;
  Description: string;
}

const RoleManagement: React.FC<RoleManagementProps> = ({ user, action, onUserUpdate, onRequestCreated }) => {
  const [availableRoles, setAvailableRoles] = useState<SapRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load SAP roles from API
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setRolesLoading(true);
        setRolesError(null);
        
        // Call the function_get_sap_roles API
        const baseUrl = process.env.REACT_APP_FUNCTION_URL || 'https://af-xen-eu-nyx-prd-gtc0gbh7bxazhnec.westeurope-01.azurewebsites.net/api';
        const functionKey = process.env.REACT_APP_FUNCTION_KEY || null;
        let url = `${baseUrl}/function_get_sap_roles`;
        if (functionKey) {
          url += `?code=${functionKey}`;
        }
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.Success && result.Roles) {
          // Store full role objects with description
          setAvailableRoles(result.Roles);
          console.log('Loaded SAP roles:', result.Roles);
        } else {
          throw new Error(result.Message || 'Failed to load roles');
        }
      } catch (error) {
        console.error('Error loading SAP roles:', error);
        setRolesError(error instanceof Error ? error.message : 'Failed to load roles');
        // Fallback to empty array
        setAvailableRoles([]);
      } finally {
        setRolesLoading(false);
      }
    };

    loadRoles();
  }, []);
  const [magicLinks, setMagicLinks] = useState<{approveUrl: string, rejectUrl: string, baseUrl: string} | null>(null);
  const [selectedToAdd, setSelectedToAdd] = useState<string[]>([]);
  const [selectedToRemove, setSelectedToRemove] = useState<string[]>([]);
  const [oldRoleName, setOldRoleName] = useState<string>('');
  const [newRoleName, setNewRoleName] = useState<string>('');


  const submitAddRoles = async () => {
    if (selectedToAdd.length === 0) return;
    
    setIsLoading(true);
    setMessage(null);

    const userEmail = localAuthService.getUserEmail();
    const userName = localAuthService.getUserDisplayName();

    const approvalRequest: ApprovalRequest = {
      id: `req_${Date.now()}`,
      userId: user.id,
      userName: user.name,
      requestedBy: userName,
      requestedByEmail: userEmail || '',
      managerEmail: user.manager.email,
      managerName: user.manager.name,
      action: 'add',
      roles: selectedToAdd,
      currentRoles: user.currentRoles,
      status: 'pending',
      requestedAt: new Date()
    };

    try {
      const emailSent = await EmailService.sendApprovalEmail(approvalRequest);
      
      if (emailSent) {
        onRequestCreated(approvalRequest);
        
        const urls = ApprovalTokenManager.generateApprovalUrls(
          approvalRequest.id,
          approvalRequest.managerEmail,
          approvalRequest.userId,
          approvalRequest.action,
          approvalRequest.roles
        );
        
        setMagicLinks(urls);
        setMessage({
          type: 'success',
          text: `Request to add ${selectedToAdd.length} role(s) sent to ${user.manager.name}. Test the approval link below.`
        });
        
        setSelectedToAdd([]);
      } else {
        setMessage({
          type: 'error',
          text: 'Failed to send approval email. Please try again or contact support.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to send approval email. Please try again or contact support.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitRemoveRoles = async () => {
    if (selectedToRemove.length === 0) return;
    
    setIsLoading(true);
    setMessage(null);

    const userEmail = localAuthService.getUserEmail();
    const userName = localAuthService.getUserDisplayName();

    const approvalRequest: ApprovalRequest = {
      id: `req_${Date.now()}`,
      userId: user.id,
      userName: user.name,
      requestedBy: userName,
      requestedByEmail: userEmail || '',
      managerEmail: user.manager.email,
      managerName: user.manager.name,
      action: 'remove',
      roles: selectedToRemove,
      currentRoles: user.currentRoles,
      status: 'pending',
      requestedAt: new Date()
    };

    try {
      const emailSent = await EmailService.sendApprovalEmail(approvalRequest);
      
      if (emailSent) {
        onRequestCreated(approvalRequest);
        
        const urls = ApprovalTokenManager.generateApprovalUrls(
          approvalRequest.id,
          approvalRequest.managerEmail,
          approvalRequest.userId,
          approvalRequest.action,
          approvalRequest.roles
        );
        
        setMagicLinks(urls);
        setMessage({
          type: 'success',
          text: `Request to remove ${selectedToRemove.length} role(s) sent to ${user.manager.name}. Test the approval link below.`
        });
        
        setSelectedToRemove([]);
      } else {
        setMessage({
          type: 'error',
          text: 'Failed to send approval email. Please try again or contact support.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to send approval email. Please try again or contact support.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitChangeRole = async () => {
    if (!oldRoleName || !newRoleName) return;
    
    setIsLoading(true);
    setMessage(null);

    try {
      const userEmail = localAuthService.getUserEmail();
      const userName = localAuthService.getUserDisplayName();

      const requestData = {
        requestType: 'ChangeRole',
        userId: user.id,
        userDisplayName: user.name,
        sapUsername: user.sapId,
        roleId: newRoleName,
        roleName: newRoleName,
        oldRoleId: oldRoleName,
        oldRoleName: oldRoleName,
        requestedBy: userEmail || userName,
        justification: `Request to change role from ${oldRoleName} to ${newRoleName} for ${user.name}`,
        managerEmail: user.manager.email,
        managerName: user.manager.name
      };

      console.log('Sending change role approval request:', requestData);
      
      const result = await approvalAPI.sendApprovalRequest(requestData);
      
      if (result.success) {
        const approvalRequest: ApprovalRequest = {
          id: result.requestId || `req_${Date.now()}`,
          userId: user.id,
          userName: user.name,
          requestedBy: userName,
          requestedByEmail: userEmail || '',
          managerEmail: user.manager.email,
          managerName: user.manager.name,
          action: 'change',
          roles: [newRoleName],
          currentRoles: user.currentRoles,
          status: 'pending',
          requestedAt: new Date()
        };
        
        onRequestCreated(approvalRequest);
        
        setMessage({
          type: 'success',
          text: `Change role request sent to ${user.manager.name} (${user.manager.email}). Request ID: ${result.requestId}`
        });
        
        setOldRoleName('');
        setNewRoleName('');
      } else {
        setMessage({
          type: 'error',
          text: `Failed to send approval request: ${result.message}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to send approval request. Please try again or contact support.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setMessage(null);

    const userEmail = localAuthService.getUserEmail();
    const userName = localAuthService.getUserDisplayName();

    // Create approval request
    const approvalRequest: ApprovalRequest = {
      id: `req_${Date.now()}`,
      userId: user.id,
      userName: user.name,
      requestedBy: userName,
      requestedByEmail: userEmail || '',
      managerEmail: user.manager.email,
      managerName: user.manager.name,
      action: action as 'add' | 'remove' | 'change',
      roles: selectedRoles,
      currentRoles: user.currentRoles,
      status: 'pending',
      requestedAt: new Date()
    };

    // Send approval request to your function
    try {
      const requestData = {
        requestType: action === 'add' ? 'AddRole' : action === 'remove' ? 'RemoveRole' : 'ChangeRole',
        userId: user.id,
        userDisplayName: user.name,
        sapUsername: user.sapId,
        roleId: selectedRoles[0] || '',
        roleName: selectedRoles[0] || '',
        oldRoleId: '',
        oldRoleName: '',
        requestedBy: userEmail || userName,
        justification: `Request to ${action} role for ${user.name}`,
        managerEmail: user.manager.email,
        managerName: user.manager.name
      };

      console.log('Sending approval request:', requestData);
      
      const result = await approvalAPI.sendApprovalRequest(requestData);
      
      if (result.success) {
        // Add request to the global requests list
        onRequestCreated(approvalRequest);
        
        setMessage({
          type: 'success',
          text: `Approval request sent to ${user.manager.name} (${user.manager.email}). Request ID: ${result.requestId}`
        });
      } else {
        setMessage({
          type: 'error',
          text: `Failed to send approval request: ${result.message}`
        });
      }
      
      setSelectedRoles([]);
      setIsEditing(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to send approval email. Please try again or contact support.'
      });
    } finally {
      setIsLoading(false);
    }
  };


  const getActionDescription = () => {
    switch (action) {
      case 'add':
        return 'Select roles to add to this user (requires manager approval)';
      case 'remove':
        return 'Select roles to remove from this user (requires manager approval)';
      case 'change':
        return 'Select the new roles for this user (will replace all current roles, requires manager approval)';
      case 'view':
        return 'View current roles for this user';
    }
  };

  const getActionButtonText = () => {
    switch (action) {
      case 'add':
        return 'Request Role Addition';
      case 'remove':
        return 'Request Role Removal';
      case 'change':
        return 'Request Role Change';
      case 'view':
        return 'Edit Roles';
    }
  };

  return (
    <div className="space-y-6">
      {/* User Info Card */}
      <div className="card">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-primary-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{user.name}</h3>
            <div className="mt-2 space-y-1">
              <div className="flex items-center text-sm text-gray-600">
                <Mail size={16} className="mr-2" />
                {user.email}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <User size={16} className="mr-2" />
                SAP ID: {user.sapId}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Building size={16} className="mr-2" />
                {user.department}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <UserCheck size={16} className="mr-2" />
                Manager: {user.manager.name} ({user.manager.email})
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="text-right">
              <div className="text-sm text-gray-500">Current Roles</div>
              <div className="text-lg font-semibold text-gray-900">
                {user.currentRoles.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Roles */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center">
            <Shield size={20} className="mr-2" />
            Current Roles
          </h4>
          <button
            onClick={async () => {
              if (user.sapId) {
                try {
                  console.log('Fetching current roles for user:', user.sapId);
                  const rolesResult = await approvalAPI.getCurrentSapRoles(user.sapId);
                  
                  // Extract role names from the response
                  // The API now returns an array of objects with agrName field, or wrapped in Success/Roles
                  let currentRoles: string[] = [];
                  
                  // Handle different response formats
                  let rolesData: any = null;
                  if (Array.isArray(rolesResult)) {
                    // Response is directly an array (raw backend response)
                    rolesData = rolesResult;
                  } else if (rolesResult.Roles) {
                    // Response has Roles field
                    rolesData = rolesResult.Roles;
                  }
                  
                  if (Array.isArray(rolesData)) {
                    // New format: array of role objects
                    currentRoles = rolesData
                      .map((role: any) => {
                        // Handle various possible field names for role name
                        if (typeof role === 'string') {
                          return role;
                        }
                        // Try different possible field names (agrName from SAP, RoleName if transformed)
                        const roleName = role.agrName || role.RoleName || role.roleName || role.name || '';
                        // Make absolutely sure we return a string, not an object
                        return typeof roleName === 'string' ? roleName : '';
                      })
                      .filter((name: string) => name && typeof name === 'string' && name.trim().length > 0); // Ensure we only have non-empty strings
                  } else if (rolesData && typeof rolesData === 'object' && !Array.isArray(rolesData)) {
                    // Old format: object with key-value pairs
                    currentRoles = Object.values(rolesData).filter((v): v is string => typeof v === 'string');
                  }
                  
                  console.log('Current roles extracted:', currentRoles);
                  
                  // Update the user object with current roles (always update if we got any data)
                  if (currentRoles.length > 0 || rolesResult.Success === false) {
                    const userWithRoles = {
                      ...user,
                      currentRoles: currentRoles
                    };
                    onUserUpdate(userWithRoles);
                  }
                  
                  if (rolesResult.Success === false) {
                    console.log('Failed to get current roles');
                    setMessage({
                      type: 'error',
                      text: 'Failed to fetch current roles. Please try again.'
                    });
                  }
                } catch (error) {
                  console.error('Error fetching current roles:', error);
                  setMessage({
                    type: 'error',
                    text: 'Error fetching current roles. Please try again.'
                  });
                }
              } else {
                setMessage({
                  type: 'error',
                  text: 'No SAP ID available. Please search for the user again.'
                });
              }
            }}
            disabled={!user.sapId}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Shield size={16} className="mr-1" />
            Get Current Roles
          </button>
        </div>
        {user.currentRoles.length > 0 ? (
          <div className="space-y-2">
            <select
              defaultValue=""
              onChange={() => {}} // Prevent any action on selection
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 cursor-pointer"
            >
              <option value="">-- Current Roles ({user.currentRoles.length}) --</option>
              {user.currentRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              {action === 'add' || action === 'change' 
                ? 'View current roles (read-only)' 
                : 'Click dropdown to view all current roles'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <select
              defaultValue=""
              onChange={() => {}} // Prevent any action on selection
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 cursor-pointer"
            >
              <option value="">-- No roles assigned --</option>
            </select>
            <p className="text-xs text-gray-500">No roles currently assigned to this user</p>
          </div>
        )}
      </div>

      {/* Role Management */}
      {action !== 'view' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">
                {action === 'add' && 'Add Roles'}
                {action === 'remove' && 'Remove Roles'}
                {action === 'change' && 'Change Roles'}
              </h4>
              <p className="text-gray-600">{getActionDescription()}</p>
            </div>
          </div>

          {action === 'add' ? (
            <div className="space-y-4">
              {rolesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading SAP roles...</p>
                </div>
              ) : rolesError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Error loading roles</h3>
                      <p className="text-sm text-red-700 mt-1">{rolesError}</p>
                    </div>
                  </div>
                </div>
              ) : availableRoles.length === 0 ? (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No roles available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="roleSelect" className="block text-sm font-medium text-gray-700 mb-2">
                      Select Role to Add
                    </label>
                    <select
                      id="roleSelect"
                      value={selectedRoles.length > 0 ? selectedRoles[0] : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          setSelectedRoles([e.target.value]);
                        } else {
                          setSelectedRoles([]);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Select a role --</option>
                      {availableRoles.map((role) => (
                        <option key={role.RoleName} value={role.RoleName}>
                          {role.RoleName} {role.Description ? `- ${role.Description}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedRoles.length > 0 && (() => {
                    const selectedRoleObj = availableRoles.find(r => r.RoleName === selectedRoles[0]);
                    return (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-800">Selected Role:</p>
                            <p className="text-sm text-blue-700">{selectedRoles[0]}</p>
                            {selectedRoleObj?.Description && (
                              <p className="text-xs text-blue-600 mt-1">{selectedRoleObj.Description}</p>
                            )}
                          </div>
                          <button
                            onClick={() => setSelectedRoles([])}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  {selectedRoles.length} role{selectedRoles.length !== 1 ? 's' : ''} selected
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setSelectedRoles([]);
                    }}
                    className="btn-secondary flex items-center"
                  >
                    <X size={16} className="mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isLoading || selectedRoles.length === 0}
                    className="btn-primary flex items-center disabled:opacity-50"
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        {getActionButtonText()}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : action === 'remove' ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="removeRoleSelect" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Role to Remove
                </label>
                <select
                  id="removeRoleSelect"
                  value={selectedToRemove.length > 0 ? selectedToRemove[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setSelectedToRemove([e.target.value]);
                    } else {
                      setSelectedToRemove([]);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">-- Select a role to remove --</option>
                  {user.currentRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedToRemove.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-800">Selected Role to Remove:</p>
                      <p className="text-sm text-red-700">{selectedToRemove[0]}</p>
                    </div>
                    <button
                      onClick={() => setSelectedToRemove([])}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  {selectedToRemove.length} role{selectedToRemove.length !== 1 ? 's' : ''} selected for removal
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setSelectedToRemove([])}
                    className="btn-secondary flex items-center"
                  >
                    <X size={16} className="mr-2" />
                    Clear Selection
                  </button>
                  <button
                    onClick={submitRemoveRoles}
                    disabled={selectedToRemove.length === 0 || isLoading}
                    className="btn-primary flex items-center disabled:opacity-50"
                  >
                    <Send size={16} className="mr-2" />
                    Request Removal
                  </button>
                </div>
              </div>
            </div>
          ) : action === 'change' ? (
            <div className="space-y-4">
              {rolesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading SAP roles...</p>
                </div>
              ) : rolesError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Error loading roles</h3>
                      <p className="text-sm text-red-700 mt-1">{rolesError}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="oldRoleSelect" className="block text-sm font-medium text-gray-700 mb-2">
                      Select Current Role to Change
                    </label>
                    <select
                      id="oldRoleSelect"
                      value={oldRoleName}
                      onChange={(e) => setOldRoleName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">-- Select current role --</option>
                      {user.currentRoles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="newRoleSelect" className="block text-sm font-medium text-gray-700 mb-2">
                      Select New Role
                    </label>
                    <select
                      id="newRoleSelect"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Select new role --</option>
                      {availableRoles
                        .filter(role => role.RoleName !== oldRoleName)
                        .map((role) => (
                          <option key={role.RoleName} value={role.RoleName}>
                            {role.RoleName} {role.Description ? `- ${role.Description}` : ''}
                          </option>
                        ))}
                    </select>
                  </div>

                  {oldRoleName && newRoleName && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800">Role Change Summary:</p>
                          <p className="text-sm text-blue-700 mt-1">
                            <span className="line-through text-red-600">{oldRoleName}</span>
                            {' → '}
                            <span className="text-green-600 font-semibold">{newRoleName}</span>
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setOldRoleName('');
                            setNewRoleName('');
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      {oldRoleName && newRoleName ? 'Ready to submit change request' : 'Select both roles to continue'}
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setOldRoleName('');
                          setNewRoleName('');
                        }}
                        className="btn-secondary flex items-center"
                      >
                        <X size={16} className="mr-2" />
                        Clear Selection
                      </button>
                      <button
                        onClick={submitChangeRole}
                        disabled={!oldRoleName || !newRoleName || isLoading}
                        className="btn-primary flex items-center disabled:opacity-50"
                      >
                        {isLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Send size={16} className="mr-2" />
                        )}
                        Request Change
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Click "Edit Roles" to make changes
            </div>
          )}
        </div>
      )}

      {/* Interactive Role Management */}
      {action === 'view' && (
        <div className="card">
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Manage Roles</h4>
            <p className="text-gray-600">Add or remove roles directly from this view</p>
          </div>

          <div className="space-y-6">
            {/* Add Roles Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-900 flex items-center">
                  <Plus className="h-4 w-4 mr-2 text-green-600" />
                  Add New Roles
                  {selectedToAdd.length > 0 && (
                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      {selectedToAdd.length} selected
                    </span>
                  )}
                </h5>
                {selectedToAdd.length > 0 && (
                  <button
                    onClick={submitAddRoles}
                    disabled={isLoading}
                    className="btn-primary flex items-center text-sm disabled:opacity-50"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Request {selectedToAdd.length} Role{selectedToAdd.length !== 1 ? 's' : ''}
                  </button>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="addRoleSelect" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Role to Add
                  </label>
                  <select
                    id="addRoleSelect"
                    value={selectedToAdd.length > 0 ? selectedToAdd[0] : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        setSelectedToAdd([e.target.value]);
                      } else {
                        setSelectedToAdd([]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">-- Select a role to add --</option>
                      {availableRoles
                        .filter(role => !user.currentRoles.includes(role.RoleName))
                        .map((role) => (
                          <option key={role.RoleName} value={role.RoleName}>
                            {role.RoleName} {role.Description ? `- ${role.Description}` : ''}
                          </option>
                        ))}
                    </select>
                </div>
                
                {selectedToAdd.length > 0 && (() => {
                  const selectedRoleObj = availableRoles.find(r => r.RoleName === selectedToAdd[0]);
                  return (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">Selected Role to Add:</p>
                          <p className="text-sm text-green-700">{selectedToAdd[0]}</p>
                          {selectedRoleObj?.Description && (
                            <p className="text-xs text-green-600 mt-1">{selectedRoleObj.Description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => setSelectedToAdd([])}
                          className="text-green-600 hover:text-green-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Remove Roles Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-900 flex items-center">
                  <Minus className="h-4 w-4 mr-2 text-red-600" />
                  Remove Current Roles
                  {selectedToRemove.length > 0 && (
                    <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                      {selectedToRemove.length} selected
                    </span>
                  )}
                </h5>
                {selectedToRemove.length > 0 && (
                  <button
                    onClick={submitRemoveRoles}
                    disabled={isLoading}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 flex items-center text-sm"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Request {selectedToRemove.length} Removal{selectedToRemove.length !== 1 ? 's' : ''}
                  </button>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="removeRoleSelect" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Role to Remove
                  </label>
                  <select
                    id="removeRoleSelect"
                    value={selectedToRemove.length > 0 ? selectedToRemove[0] : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        setSelectedToRemove([e.target.value]);
                      } else {
                        setSelectedToRemove([]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="">-- Select a role to remove --</option>
                    {user.currentRoles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedToRemove.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-red-800">Selected Role to Remove:</p>
                        <p className="text-sm text-red-700">{selectedToRemove[0]}</p>
                      </div>
                      <button
                        onClick={() => setSelectedToRemove([])}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bulk Actions */}
            <div className="pt-4 border-t border-gray-200">
              <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                <Edit3 className="h-4 w-4 mr-2 text-blue-600" />
                Bulk Actions
              </h5>
              <div className="flex space-x-3">
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-secondary flex items-center"
                >
                  <Edit3 size={16} className="mr-2" />
                  Change All Roles
                </button>
                <button
                  onClick={() => {
                    setSelectedRoles([]);
                    setIsEditing(true);
                  }}
                  className="btn-secondary flex items-center"
                >
                  <Shield size={16} className="mr-2" />
                  Replace All Roles
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Magic Links Testing Section */}
      {magicLinks && (
        <div className="card bg-blue-50 border-blue-200">
          <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
            <Send className="h-5 w-5 mr-2" />
            Magic Link Testing
          </h4>
          <p className="text-blue-800 mb-4">
            Magic links have been generated for testing. Click the buttons below to test the approval flow:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h5 className="font-medium text-gray-900 mb-2">Quick Approve</h5>
              <a 
                href={magicLinks.approveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve Request
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h5 className="font-medium text-gray-900 mb-2">Quick Reject</h5>
              <a 
                href={magicLinks.rejectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject Request
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h5 className="font-medium text-gray-900 mb-2">Full Details</h5>
              <a 
                href={magicLinks.baseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                <Eye className="h-4 w-4 mr-1" />
                View Details
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg border border-blue-200">
            <h6 className="font-medium text-gray-900 mb-2">Magic Link URLs:</h6>
            <div className="space-y-2 text-xs">
              <div className="flex items-center">
                <span className="font-medium text-gray-700 w-20">Approve:</span>
                <code className="flex-1 bg-gray-100 p-1 rounded text-gray-800">{magicLinks.approveUrl}</code>
                <button 
                  onClick={() => navigator.clipboard.writeText(magicLinks.approveUrl)}
                  className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
              <div className="flex items-center">
                <span className="font-medium text-gray-700 w-20">Reject:</span>
                <code className="flex-1 bg-gray-100 p-1 rounded text-gray-800">{magicLinks.rejectUrl}</code>
                <button 
                  onClick={() => navigator.clipboard.writeText(magicLinks.rejectUrl)}
                  className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
              <div className="flex items-center">
                <span className="font-medium text-gray-700 w-20">Details:</span>
                <code className="flex-1 bg-gray-100 p-1 rounded text-gray-800">{magicLinks.baseUrl}</code>
                <button 
                  onClick={() => navigator.clipboard.writeText(magicLinks.baseUrl)}
                  className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-700' 
            : 'bg-red-50 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <Check size={16} className="mr-2" />
          ) : (
            <AlertCircle size={16} className="mr-2" />
          )}
          {message.text}
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
